import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Upload, X, Camera, Sparkles, ScanLine } from "lucide-react";
import endlessStorageLogo from "@/assets/endless-storage-logo.png";
import { useToast } from "@/hooks/use-toast";
import { fileToBase64 } from "@/lib/qr-utils";
import { CameraCapture } from "@/components/CameraCapture";
import { AIItemsReview } from "@/components/AIItemsReview";
import { QRScanner } from "@/components/QRScanner";
import { analyzeBoxContents } from "@/lib/ai-vision";

const ROOMS = [
  "Living Room",
  "Bedroom",
  "Kitchen",
  "Bathroom",
  "Garage",
  "Attic",
  "Basement",
  "Office",
  "Dining Room",
  "Kids Room",
  "Guest Room",
  "Other",
];

interface DetectedItem {
  name: string;
  quantity: number;
  room: string;
  notes?: string;
  aiGenerated?: boolean;
}

const BoxNewConnected = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [boxNumber, setBoxNumber] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [room, setRoom] = useState("Other");
  const [photos, setPhotos] = useState<Array<{ url: string; thumbnailUrl: string }>>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisStartTime, setAnalysisStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showSkipButton, setShowSkipButton] = useState(false);
  const [detectedItems, setDetectedItems] = useState<DetectedItem[]>([]);
  const [showItemsReview, setShowItemsReview] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    checkBoxSlotAvailability();
  }, []);

  const checkBoxSlotAvailability = async () => {
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (!currentUser) return;
    
    setUser(currentUser);

    const { data: profileData } = await supabase
      .from('profiles')
      .select('onboarding_type, box_slots_purchased, box_slots_used')
      .eq('id', currentUser.id)
      .single();

    setProfile(profileData);

    if (profileData?.onboarding_type === 'direct_storage_purchase') {
      if (profileData.box_slots_used >= profileData.box_slots_purchased) {
        toast({
          title: "Box Slot Limit Reached",
          description: `You've used all ${profileData.box_slots_purchased} box slots. Upgrade your plan to add more boxes.`,
          variant: "destructive",
          duration: 5000
        });
        navigate('/dashboard');
      }
    }
  };

  const generateBoxNameSuggestion = (items: DetectedItem[]): string => {
    if (items.length === 0) return "";
    
    const roomCounts = items.reduce((acc, item) => {
      acc[item.room] = (acc[item.room] || 0) + item.quantity;
      return acc;
    }, {} as Record<string, number>);
    
    const dominantRoom = Object.entries(roomCounts)
      .sort(([,a], [,b]) => b - a)[0]?.[0];
    
    if (dominantRoom) {
      return `${dominantRoom} Items`;
    }
    
    return "Mixed Household Items";
  };

  const getMostCommonRoom = (items: DetectedItem[]): string => {
    const roomCounts = items.reduce((acc, item) => {
      acc[item.room] = (acc[item.room] || 0) + item.quantity;
      return acc;
    }, {} as Record<string, number>);
    
    return Object.entries(roomCounts)
      .sort(([,a], [,b]) => b - a)[0]?.[0] || "Other";
  };

  const handleSkipAnalysis = () => {
    setIsAnalyzing(false);
    setAnalysisStartTime(null);
    setElapsedTime(0);
    setShowSkipButton(false);
    
    toast({
      title: "Analysis skipped",
      description: "You can add box details manually below.",
    });
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setIsAnalyzing(true);
    setAnalysisStartTime(Date.now());
    setElapsedTime(0);
    
    // Start elapsed time counter
    const intervalId = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
    
    // Show skip button after 3 seconds
    const skipTimeout = setTimeout(() => setShowSkipButton(true), 3000);

    try {
      const file = files[0];
      const imageData = await fileToBase64(file);
      
      setPhotos(prev => [...prev, {
        url: imageData,
        thumbnailUrl: imageData,
      }]);

      try {
        const items = await analyzeBoxContents(imageData);
        
        clearInterval(intervalId);
        clearTimeout(skipTimeout);
        setIsAnalyzing(false);
        setAnalysisStartTime(null);
        setElapsedTime(0);
        setShowSkipButton(false);
        
        if (items.length > 0) {
          const itemsList = items.map(item => ({
            ...item,
            aiGenerated: true,
          }));
          
          setDetectedItems(itemsList);
          
          const formattedDescription = formatItemsDescription(items);
          setDescription(formattedDescription);
          
          const suggestedName = generateBoxNameSuggestion(items);
          setName(suggestedName);
          
          const suggestedRoom = getMostCommonRoom(items);
          setRoom(suggestedRoom);
          
          setShowItemsReview(true);
          
          toast({
            title: "Smart Scan Complete! ✨",
            description: `Found ${items.length} items. Name and room auto-filled!`,
          });
        } else {
          toast({
            title: "No Items Detected",
            description: "No items found. You can add items manually later.",
          });
        }
      } catch (analysisError) {
        clearInterval(intervalId);
        clearTimeout(skipTimeout);
        setIsAnalyzing(false);
        setAnalysisStartTime(null);
        setElapsedTime(0);
        setShowSkipButton(false);
        
        console.error('AI analysis error:', analysisError);
        
        const errorMessage = analysisError instanceof Error ? analysisError.message : 'Unknown error';
        if (errorMessage.includes('timed out')) {
          toast({
            title: "Analysis timed out",
            description: "The AI took too long to analyze your photo. Photo saved - you can add items manually.",
            variant: "destructive",
          });
        } else if (errorMessage.includes('Rate limit') || errorMessage.includes('429')) {
          toast({
            title: "Rate limit reached",
            description: "Too many analysis requests. Please try again in a few minutes.",
            variant: "destructive",
          });
        } else if (errorMessage.includes('credits') || errorMessage.includes('402')) {
          toast({
            title: "AI credits depleted",
            description: "Please add credits to your workspace to continue using AI analysis.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Analysis failed",
            description: "Photo saved. You can add items manually or try uploading a clearer photo.",
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      clearInterval(intervalId);
      clearTimeout(skipTimeout);
      setIsAnalyzing(false);
      setAnalysisStartTime(null);
      setElapsedTime(0);
      setShowSkipButton(false);
      
      console.error('File reading error:', error);
      toast({
        title: "Upload Error",
        description: "Failed to read the photo. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const formatItemsDescription = (items: Array<{name: string; quantity: number; room: string; notes?: string}>) => {
    const grouped = items.reduce((acc, item) => {
      if (!acc[item.room]) {
        acc[item.room] = [];
      }
      acc[item.room].push(item);
      return acc;
    }, {} as Record<string, Array<{name: string; quantity: number; room: string; notes?: string}>>);

    let description = "📦 Box Contents:\n";
    
    Object.entries(grouped).forEach(([room, roomItems]) => {
      const totalCount = roomItems.reduce((sum, item) => sum + item.quantity, 0);
      description += `- **${room} (${totalCount} total):**\n`;
      
      roomItems.forEach(item => {
        const itemName = item.notes ? `${item.name} — ${item.notes}` : item.name;
        if (item.quantity > 1) {
          description += `  - ${item.quantity}x ${itemName}\n`;
        } else {
          description += `  - ${itemName}\n`;
        }
      });
    });

    return description;
  };

  const removePhoto = (index: number) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const handleCameraCapture = async (imageData: string) => {
    setIsAnalyzing(true);
    setAnalysisStartTime(Date.now());
    setElapsedTime(0);
    
    const intervalId = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
    
    const skipTimeout = setTimeout(() => setShowSkipButton(true), 3000);
    
    try {
      const items = await analyzeBoxContents(imageData);
      
      clearInterval(intervalId);
      clearTimeout(skipTimeout);
      setIsAnalyzing(false);
      setAnalysisStartTime(null);
      setElapsedTime(0);
      setShowSkipButton(false);
      
      if (items.length === 0) {
        toast({
          title: "No Items Detected",
          description: "Try retaking the photo with better lighting or add items manually.",
        });
        return;
      }

      setPhotos(prev => [...prev, {
        url: imageData,
        thumbnailUrl: imageData,
      }]);

      const itemsList = items.map(item => ({
        ...item,
        aiGenerated: true,
      }));
      
      setDetectedItems(itemsList);

      const formattedDescription = formatItemsDescription(items);
      setDescription(formattedDescription);
      
      const suggestedName = generateBoxNameSuggestion(items);
      setName(suggestedName);
      
      const suggestedRoom = getMostCommonRoom(items);
      setRoom(suggestedRoom);
      
      setShowItemsReview(true);

      toast({
        title: "Smart Scan Complete! ✨",
        description: `Found ${items.length} items. Name and room auto-filled!`,
      });
    } catch (error) {
      clearInterval(intervalId);
      clearTimeout(skipTimeout);
      setIsAnalyzing(false);
      setAnalysisStartTime(null);
      setElapsedTime(0);
      setShowSkipButton(false);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('timed out')) {
        toast({
          title: "Analysis timed out",
          description: "The AI took too long. Photo saved - you can add items manually.",
          variant: "destructive",
        });
      } else if (errorMessage.includes('Rate limit') || errorMessage.includes('429')) {
        toast({
          title: "Rate limit reached",
          description: "Too many requests. Please try again in a few minutes.",
          variant: "destructive",
        });
      } else if (errorMessage.includes('credits') || errorMessage.includes('402')) {
        toast({
          title: "AI credits depleted",
          description: "Please add credits to your workspace.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Analysis Failed",
          description: error instanceof Error ? error.message : "Failed to analyze photo. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  const handleItemsConfirm = (items: DetectedItem[]) => {
    setDetectedItems(items);
    setShowItemsReview(false);
  };

  const handleQRScan = async (scannedBoxNumber: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: existingBoxes } = await supabase
        .from('boxes')
        .select('box_number')
        .eq('user_id', user.id)
        .eq('box_number', scannedBoxNumber);

      if (existingBoxes && existingBoxes.length > 0) {
        toast({
          title: "Duplicate Box Number",
          description: "This box number already exists. Please scan a different label.",
          variant: "destructive",
        });
        return;
      }
      
      setBoxNumber(scannedBoxNumber);
      toast({
        title: "QR Code Scanned!",
        description: `Box number ${scannedBoxNumber} assigned.`,
      });
    } catch (error) {
      console.error('Error checking box number:', error);
    }
  };

  const uploadPhotoToStorage = async (base64Image: string, boxId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Convert base64 to blob
      const base64Data = base64Image.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/jpeg' });

      // Upload to storage
      const fileName = `${user.id}/${boxId}/${Date.now()}.jpg`;
      const { error: uploadError } = await supabase.storage
        .from('box-photos')
        .upload(fileName, blob);

      if (uploadError) throw uploadError;

      // Create photo record
      const { error: photoError } = await supabase
        .from('photos')
        .insert({
          box_id: boxId,
          storage_path: fileName,
        });

      if (photoError) throw photoError;
    } catch (error) {
      console.error('Error uploading photo:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!boxNumber.trim()) {
      toast({
        title: "Box Number Required",
        description: "Please enter or scan a box number.",
        variant: "destructive",
      });
      return;
    }
    
    if (!name.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter a box name.",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Check for duplicate box number in database
      const { data: existingBoxes } = await supabase
        .from('boxes')
        .select('id')
        .eq('user_id', user.id)
        .eq('box_number', boxNumber.trim());

      if (existingBoxes && existingBoxes.length > 0) {
        toast({
          title: "Duplicate Box Number",
          description: "This box number already exists. Please use a different one.",
          variant: "destructive",
        });
        setIsCreating(false);
        return;
      }

      // Create box in database
      const { data: newBox, error: boxError } = await supabase
        .from('boxes')
        .insert({
          user_id: user.id,
          box_number: boxNumber.trim(),
          name: name.trim(),
          description: description.trim() || null,
          room: room,
          location_status: 'home',
        })
        .select()
        .single();

      if (boxError) throw boxError;

      // Upload photos to storage
      if (photos.length > 0) {
        for (const photo of photos) {
          try {
            await uploadPhotoToStorage(photo.url, newBox.id);
          } catch (error) {
            console.error('Error uploading photo:', error);
          }
        }
      }

      // Add detected items if any
      if (detectedItems.length > 0) {
        const itemsToInsert = detectedItems.map(item => ({
          box_id: newBox.id,
          name: item.name,
          quantity: item.quantity,
          room: item.room,
          notes: item.notes || null,
          ai_generated: item.aiGenerated || false,
        }));

        const { error: itemsError } = await supabase
          .from('items')
          .insert(itemsToInsert);

        if (itemsError) {
          console.error('Error adding items:', itemsError);
        }
      }

      toast({
        title: "Box Created!",
        description: `Box ${boxNumber} has been created successfully.`,
      });

      navigate(`/box/${newBox.id}`);
    } catch (error) {
      console.error('Error creating box:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create box",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <header className="bg-card border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <button 
              onClick={() => navigate('/')}
              className="hover:opacity-80 transition-opacity"
            >
              <img src={endlessStorageLogo} alt="Endless Storage" className="h-10" />
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card className="shadow-elegant">
          <CardHeader>
            <CardTitle>Box Details</CardTitle>
            <CardDescription>
              Add information about your box. You can add items later.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Smart Scan - AI First Workflow */}
              <div className="space-y-4 p-6 border-2 border-primary/30 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Sparkles className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <Label className="text-lg font-semibold">Quick Start: Let AI Fill Everything</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Take a photo of your box contents. AI will auto-fill the name, room, and description for you!
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-auto py-6 flex-col gap-2 border-primary/50 hover:border-primary hover:bg-primary/10"
                    onClick={() => setShowCamera(true)}
                    disabled={isAnalyzing || isUploading}
                  >
                    <Camera className="h-6 w-6 text-primary" />
                    <span className="text-sm font-medium">Take Photo</span>
                  </Button>

                  <Label htmlFor="smart-photo-upload" className="cursor-pointer">
                    <div className="h-full py-6 flex flex-col items-center justify-center gap-2 border border-primary/50 rounded-md hover:border-primary hover:bg-primary/10 transition-colors">
                      <Upload className="h-6 w-6 text-primary" />
                      <span className="text-sm font-medium">Upload Photo</span>
                    </div>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      disabled={isAnalyzing || isUploading}
                      className="hidden"
                      id="smart-photo-upload"
                    />
                  </Label>
                </div>

                {isAnalyzing && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 animate-pulse text-primary" />
                        <span className="text-primary font-medium">Analyzing photo... ({elapsedTime}s)</span>
                      </div>
                      {showSkipButton && (
                        <Button 
                          type="button"
                          variant="ghost" 
                          size="sm" 
                          onClick={handleSkipAnalysis}
                          className="text-primary hover:text-primary/80"
                        >
                          Skip AI Analysis
                        </Button>
                      )}
                    </div>
                    <Progress value={(elapsedTime / 30) * 100} className="h-2" />
                    {elapsedTime > 10 && (
                      <p className="text-xs text-amber-600">
                        This is taking longer than usual. You can skip and add items manually.
                      </p>
                    )}
                  </div>
                )}
                
                {detectedItems.length > 0 && (
                  <div className="p-3 bg-primary/10 border border-primary/20 rounded-md">
                    <p className="text-sm text-primary font-medium">
                      ✨ {detectedItems.length} items detected • Name & room auto-filled!
                    </p>
                  </div>
                )}
              </div>

              {/* Box Identification - Last step */}
              <div className="space-y-4 p-4 border border-border/50 rounded-lg bg-muted/30">
                <div>
                  <Label className="text-base font-semibold">Box Number</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Enter or scan your box number (optional - can add later)
                  </p>
                </div>
                
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="boxNumber">Box Number *</Label>
                    <Input
                      id="boxNumber"
                      placeholder="e.g., ES-1001"
                      value={boxNumber}
                      onChange={(e) => setBoxNumber(e.target.value)}
                      required
                    />
                  </div>
                  
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => setShowQRScanner(true)}
                  >
                    <ScanLine className="h-4 w-4 mr-2" />
                    Scan QR Code Label
                  </Button>
                </div>
              </div>

              {/* Box Name - Auto-filled by AI */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="name">Box Name *</Label>
                  {name && detectedItems.length > 0 && (
                    <span className="text-xs text-primary font-medium px-2 py-0.5 bg-primary/10 rounded">
                      ✨ AI Suggested
                    </span>
                  )}
                </div>
                <Input
                  id="name"
                  placeholder="e.g., Kitchen Items, Winter Clothes"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <textarea
                  id="description"
                  placeholder="Describe what's in this box (auto-populated from AI scan)"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full min-h-[80px] px-3 py-2 text-sm rounded-md border border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  rows={3}
                />
              </div>

              {/* Room - Auto-selected by AI */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="room">Room</Label>
                  {room !== "Other" && detectedItems.length > 0 && (
                    <span className="text-xs text-primary font-medium px-2 py-0.5 bg-primary/10 rounded">
                      ✨ AI Suggested
                    </span>
                  )}
                </div>
                <Select value={room} onValueChange={setRoom}>
                  <SelectTrigger id="room">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROOMS.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>


              {/* Additional Photos Gallery */}
              {photos.length > 0 && (
                <div className="space-y-2">
                  <Label>Uploaded Photos</Label>
                  <div className="grid grid-cols-3 gap-4">
                    {photos.map((photo, index) => (
                      <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-muted">
                        <img src={photo.url} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute top-1 right-1"
                          onClick={() => removePhoto(index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Submit Buttons */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate('/dashboard')}
                  disabled={isCreating}
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-gradient-primary" disabled={isCreating}>
                  {isCreating ? "Creating..." : "Create Box"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Camera Modal */}
      <CameraCapture
        open={showCamera}
        onClose={() => setShowCamera(false)}
        onCapture={handleCameraCapture}
      />

      {/* QR Scanner Modal */}
      <QRScanner
        open={showQRScanner}
        onClose={() => setShowQRScanner(false)}
        onScan={handleQRScan}
      />

      {/* Items Review Modal */}
      <AIItemsReview
        open={showItemsReview}
        onClose={() => setShowItemsReview(false)}
        items={detectedItems}
        onConfirm={handleItemsConfirm}
      />
    </div>
  );
};

export default BoxNewConnected;
